<template>
  <div class="totals d-flex flex-column" :class="{ 'edit-mode': editing }">
    <div class="d-flex flex-column adjustments-cont total-collection">
      <div
        v-if="
          (editing && lateFeesEnabled) || data.financials.late_fee_cents !== 0
        "
        class="display-row fee-row"
      >
        <span>Technician Late Fee</span>
        <span v-if="!editing || techMode">{{
          currency((data.financials.late_fee_cents / 100) * -1)
        }}</span>
        <span v-else>
          <CurrencyInput
            :value="data.financials.late_fee_cents"
            class="late-fee-input"
            @price-change="handlePriceChange"
          />
        </span>
        <div
          v-if="editing && !techMode"
          class="remove-fee-icon"
          @click="handleRemove"
        >
          <slot name="deleteIcon">X</slot>
        </div>
      </div>
      <div
        v-if="
          data.discount_attributes ||
          data.financials.discount_amount_cents !== 0
        "
        class="display-row fee-row"
      >
        <span v-if="data.discount_attributes">
          <a v-if="editing" class="discount-link" @click="discountClick">
            Promo Code {{ data.discount_attributes.code }}
            {{
              data.discount_attributes.kind === "percentage"
                ? "(" + data.discount_attributes.percent + "%)"
                : ""
            }}
          </a>
          <span v-if="!editing">
            Promo Code {{ data.discount_attributes.code }}
            {{
              data.discount_attributes.kind === "percentage"
                ? "(" + data.discount_attributes.percent + "%)"
                : ""
            }}
          </span>
        </span>
        <span v-if="!data.discount_attributes">Discount</span>
        <span>{{
          currency((data.financials.discount_amount_cents / 100) * -1)
        }}</span>
        <div
          v-if="editing && data.discount_attributes.id"
          class="remove-fee-icon"
          @click="handleRemoveDiscount(data.discount_attributes.id)"
        >
          <slot name="deleteIcon">X</slot>
        </div>
      </div>
      <div v-if="!data.discount_attributes && editing" class="display-row">
        <span>
          <a href="javascript:;" class="discount-link" @click="discountClick"
            >Add Promo Code</a
          >
        </span>
        <span>--</span>
      </div>
      <div class="display-row">
        <span>
          <a
            v-if="editing && editTaxesEnabled"
            href="javascript:;"
            class="tax-link"
            @click="taxesClick"
          >
            Tax
            <i class="far fa-pen-to-square" />
          </a>
          <span v-else>Tax</span>
        </span>
        <span>{{ currency(data.financials.sales_tax_cents / 100) }}</span>
      </div>
    </div>
    <div class="total-collection">
      <div class="display-row">
        <span v-if="type === 'quote'">Estimated Total</span>
        <span v-if="type !== 'quote'">Total</span>
        <span>{{ currency(data.financials.total_cents / 100) }}</span>
      </div>

      <div v-if="type === 'invoice'">
        <div class="display-row">
          <span v-if="techMode">
            <a class="payments-link" @click="paymentsClick">Payments</a>
          </span>
          <span v-else>Payments</span>
          <span v-if="data.financials.total_transactions_cents > 0">
            {{ currency(data.financials.total_transactions_cents / 100) }}
          </span>
          <span v-if="data.financials.total_transactions_cents <= 0">--</span>
        </div>
        <div class="display-row">
          <span>Balance Remaining</span>
          <span v-if="data.financials.balance_cents > 0">{{
            currency(data.financials.balance_cents / 100)
          }}</span>
          <span v-if="data.financials.balance_cents <= 0">--</span>
        </div>
        <div
          v-if="showSpbCurrentDrawDue"
          class="spb-totals-callout spb-totals-callout--due"
          data-cy="spb-totals-current-draw-due"
        >
          <div class="spb-totals-callout__primary">
            <strong>Current Draw Due</strong>
            <strong>{{
              currency(data.progress_billing.current_draw.remaining_cents / 100)
            }}</strong>
          </div>
          <div class="spb-totals-callout__sub">
            {{ data.progress_billing.current_draw.label
            }}<template v-if="data.progress_billing.current_draw.due_date">
              · due
              {{ formatSpbDate(data.progress_billing.current_draw.due_date) }}
            </template>
          </div>
        </div>
        <div
          v-if="showSpbNoPaymentDue"
          class="spb-totals-callout spb-totals-callout--no-due"
          data-cy="spb-totals-no-payment-due"
        >
          <div class="spb-totals-callout__primary">
            No payment currently due
          </div>
          <div v-if="spbNoPaymentDueDetail" class="spb-totals-callout__sub">
            {{ spbNoPaymentDueDetail }}
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="
        !editing &&
        Math.abs(data.financials.savings_cents) > 0 &&
        data.display_membership_savings
      "
      class="savings-cont"
    >
      <HandDrawnCircle v-if="Math.abs(data.financials.savings_cents) > 0" />
      <div
        v-if="data.financials.savings_cents > 0"
        class="display-row savings-total"
      >
        <span>
          <a
            v-if="type !== 'invoice'"
            class="savings-link"
            @click="savingsClick"
            >Membership savings!</a
          >
          <span v-else>Membership Savings!</span>
        </span>
        <span>{{ currency(data.financials.savings_cents / 100) }}</span>
      </div>
      <div
        v-if="data.financials.savings_cents < 0"
        class="display-row savings-total"
      >
        <span>You could have saved:</span>
        <span>
          {{ currency(Math.abs(data.financials.savings_cents) / 100) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import CurrencyInput from "serviceshift-ui/components/Inputs/CurrencyInput.vue";
import HandDrawnCircle from "serviceshift-ui/shared/src/assets/hand_drawn_circle.svg";
import currency from "serviceshift-ui/shared/src/filters/currency";
import EventBus from "serviceshift-ui/shared/src/lib/event_bus";
import debounce from "serviceshift-ui/shared/src/mixins/debounce";

import { Invoice } from "@/typings/invoice";
import { Quote } from "@/typings/quote";

export default {
  components: {
    CurrencyInput,
    HandDrawnCircle
  },
  props: {
    data: {
      type: Object as () => Quote | Invoice,
      default: () => ({})
    },
    editing: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ""
    },
    discountId: {
      type: Number,
      default: null
    },
    lateFeesEnabled: {
      type: Boolean,
      default: false
    },
    editTaxesEnabled: {
      type: Boolean,
      default: false
    },
    techMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      HandDrawnCircle,
      moneyConfig: {
        decimal: ".",
        thousands: ",",
        prefix: "$",
        suffix: "",
        precision: 2,
        masked: false
      }
    };
  },
  computed: {
    eventId() {
      return this.data.invoice_id || this.data.id;
    },
    spbBlock() {
      return (this.data as Invoice).progress_billing ?? null;
    },
    spbActive() {
      const block = this.spbBlock;
      return Boolean(
        block && block.is_progress_billing && block.payment_status !== "voided"
      );
    },
    showSpbCurrentDrawDue() {
      const block = this.spbBlock;
      return Boolean(
        this.spbActive && block?.current_draw && block.current_draw.is_due
      );
    },
    showSpbNoPaymentDue() {
      const block = this.spbBlock;
      return Boolean(
        this.spbActive && block?.current_draw && !block.current_draw.is_due
      );
    },
    spbNoPaymentDueDetail() {
      const block = this.spbBlock;
      if (!block) return "";
      const next = block.next_draw_after_current;
      if (next) {
        const datePart = next.due_date
          ? ` · due ${this.formatSpbDate(next.due_date)}`
          : "";
        return `Next: ${next.label}${datePart}`;
      }
      // Single-installment / last-draw fallback (AC13 edge).
      const current = block.current_draw;
      if (current?.due_date) {
        return `Final draw on ${this.formatSpbDate(current.due_date)}`;
      }
      return "";
    }
  },
  methods: {
    currency,
    // Date-only strings (e.g. "2026-05-15") parse as UTC midnight, so format
    // in UTC to avoid the column shifting back a day in negative timezones.
    formatSpbDate(dueDate: string): string {
      const parsed = new Date(dueDate);
      if (Number.isNaN(parsed.getTime())) return "—";
      return parsed.toLocaleDateString("en-US", {
        year: "2-digit",
        month: "2-digit",
        day: "2-digit",
        timeZone: "UTC"
      });
    },
    handlePriceChange: debounce(function (value) {
      if (value !== this.data.financials.late_fee_cents) {
        EventBus.$emit(
          "edit-late-fee",
          {
            target: {
              value
            }
          },
          this.eventId
        );
      }
    }, 750),
    handleRemove() {
      EventBus.$emit("remove-late-fee", this.eventId);
    },
    discountClick() {
      EventBus.$emit("discount-click", this.eventId);
    },
    taxesClick() {
      EventBus.$emit("taxes-click", this.eventId);
    },
    handleRemoveDiscount(id) {
      EventBus.$emit("remove-discount", id, this.eventId);
    },
    paymentsClick() {
      EventBus.$emit("payments-click", this.eventId);
    },
    savingsClick() {
      EventBus.$emit("savings-click", this.eventId);
    }
  }
};
</script>

<style lang="scss" scoped>
.discount-select {
  justify-content: flex-end;
  align-items: center;
  span {
    &:first-of-type {
      display: flex;
      justify-content: flex-end;
      ion-item {
        background: transparent;
        width: 188px;
        font-size: 1.6rem;
        margin-right: -45px;
        height: 2rem;
        .select-icon {
          display: none;
        }
        .select-placeholder {
          color: var(--primary-color);
        }
        .item-inner {
          border-bottom: none;
        }
        ion-select {
          max-width: none;
        }
      }
    }
  }
}
.fee-row {
  position: relative;
  .remove-fee-icon {
    position: absolute;
    color: var(--primary-color);
    font-size: 1.3em !important;
    right: -36px;
    top: -3px;
    cursor: pointer;
  }
}
.total-collection {
  padding: 5px 10px 0px !important;
}
.totals {
  background-color: var(--light-color);
  .adjustments-cont {
    background-color: var(--pale-blue);
  }
}
.savings-total {
  padding: 5px 10px !important;
  font-weight: bold;
  font-style: italic;
  background-color: var(--pale-blue);
  height: 50px;
  align-items: center;
}
.savings-cont {
  position: relative;
  svg {
    position: absolute;
    width: 100px;
    height: 100%;
    right: 0;
    -webkit-animation: puff-in-center 0.5s cubic-bezier(0.47, 0, 0.745, 0.715)
      1s both;
    animation: puff-in-center 0.5s cubic-bezier(0.47, 0, 0.745, 0.715) 1s both;
  }
}
.display-row {
  display: flex;
  margin-bottom: 5px;

  span {
    text-align: right;
    width: 95px;
    &:first-of-type {
      flex: 3;
    }
  }
}

// SPB current-draw / no-payment-due totals rows. Full-width 2-line callout
// instead of reusing `.display-row`'s narrow right column — the sub-label
// ("Deposit · due 05/05/26" / "Next: Final Payment · due 06/04/26") is too
// long to wrap cleanly in the existing 95px column.
.spb-totals-callout {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid var(--primary-light-accent-color);

  &__primary {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
  }

  &__sub {
    font-size: 12px;
    color: var(--sera-color-neutral-gray-med-gray-500, #6b7280);
    margin-top: 2px;
    text-align: left;
  }
}
.late-fee-input {
  width: 80px;
  text-align: center;
  border: 1px solid var(--primary-accent-color);
  border-radius: 5px;
  min-height: 30px;
  font-family: inherit;
  padding: 0 5px;
}

.edit-mode {
  .total-collection {
    padding: 5px 46px 0px !important;
  }
  .savings-total {
    padding: 5px 50px !important;
  }
  .savings-cont {
    svg {
      right: 35px;
    }
  }
}
</style>
